// src/components/ai-prayer/companion/RiskAlertBanner.tsx
import React, { useEffect, useState } from 'react';
import type { RiskFlag } from '@/utils/risk';

interface RiskAlertBannerProps {
  riskFlag: RiskFlag;
  className?: string;
  duration?: number; // ms before disappearing (default: 6000)
}

export default function RiskAlertBanner({
  riskFlag,
  className,
  duration = 6000,
}: RiskAlertBannerProps) {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFading(true), duration - 500); // start fading 0.5s before removal
    const hideTimer = setTimeout(() => setVisible(false), duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [duration]);

  if (!visible) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`
        bg-warning/10 border border-warning/20 rounded-lg p-3 text-center mb-3 
        transition-opacity duration-500 ease-in-out 
        ${fading ? 'opacity-0' : 'opacity-100'} 
        ${className || ''}
      `}
    >
      <p className="text-sm text-warning font-semibold">
        ⚠️ Sensitive issue detected:{" "}
        <span className="uppercase tracking-wide">{riskFlag.category}</span>.
      </p>
      <p className="text-xs mt-2 text-muted-foreground">
        If you are in immediate danger, please call your local hotline (e.g.,{" "}
        <strong>988</strong> in the U.S.).
      </p>
      <p className="text-xs mt-2">
        Find international hotlines and resources here:{" "}
        <a
          href={riskFlag.link}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline font-medium text-warning hover:text-warning/80"
        >
          findahelpline.com
        </a>
      </p>
    </div>
  );
}
